
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Phone, Loader2, Settings2 } from "lucide-react";
import { LiveTranscript } from "../components/LiveTranscript";
import { VoiceAgentConfig } from "../voice/VoiceAgentConfig";
import { PhoneScreeningState } from "@/hooks/screening/types";
import { useState } from "react";
import { 
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger 
} from "@/components/ui/collapsible";

interface PhoneScreenTabProps {
  phoneScreening: PhoneScreeningState;
  isLoading: boolean;
  onStartScreening: () => Promise<void>;
}

export function PhoneScreenTab({ 
  phoneScreening, 
  isLoading,
  onStartScreening 
}: PhoneScreenTabProps) {
  const [showVoiceSettings, setShowVoiceSettings] = useState(false);

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">Live Phone Screening</h3>
      
      <div className="rounded-md border p-4 bg-muted/20 text-sm">
        <p className="text-muted-foreground">
          This feature uses AI voice technology to conduct live phone screenings with candidates. 
          The system will call the candidate, ask screening questions, and provide a summary of the call.
        </p>
      </div>
      
      <Collapsible open={showVoiceSettings} onOpenChange={setShowVoiceSettings}>
        <div className="flex items-center justify-between">
          <CollapsibleTrigger asChild>
            <Button variant="outline" size="sm" className="flex items-center gap-1">
              <Settings2 className="h-4 w-4" />
              {showVoiceSettings ? 'Hide Voice Settings' : 'Configure Voice'}
            </Button>
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent className="mt-4">
          <VoiceAgentConfig />
        </CollapsibleContent>
      </Collapsible>
      
      {phoneScreening.status === 'idle' && (
        <Button onClick={onStartScreening} disabled={isLoading} className="w-full sm:w-auto">
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Starting...
            </>
          ) : (
            <>
              <Phone className="mr-2 h-4 w-4" />
              Start Screening Call
            </>
          )}
        </Button>
      )}
      
      {phoneScreening.status === 'connecting' && (
        <div className="space-y-2">
          <Badge variant="secondary">Connecting to candidate...</Badge>
          <p className="text-sm text-muted-foreground animate-pulse">Initializing voice agent...</p>
        </div>
      )}
      
      {phoneScreening.status === 'in-progress' && (
        <div className="space-y-2">
          <Badge variant="secondary" className="animate-pulse">Call In Progress</Badge>
          <LiveTranscript 
            transcript={phoneScreening.liveTranscript || ''}
            isLive={true}
          />
        </div>
      )}
      
      {phoneScreening.status === 'completed' && (
        <div className="space-y-4">
          <Badge variant="success">Call Completed</Badge>
          
          {phoneScreening.transcript && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Call Transcript</h4>
              <LiveTranscript 
                transcript={phoneScreening.transcript} 
                isLive={false}
              />
            </div>
          )}
          
          {phoneScreening.summary && (
            <div className="space-y-2 border-t pt-4">
              <h4 className="text-sm font-medium">Call Summary</h4>
              <div className="grid gap-2 text-sm">
                <div>
                  <span className="font-medium">Overall Recommendation:</span> {' '}
                  <Badge variant={
                    phoneScreening.summary.overallRecommendation === 'strong_yes' || 
                    phoneScreening.summary.overallRecommendation === 'yes' ? 'success' : 
                    phoneScreening.summary.overallRecommendation === 'maybe' ? 'warning' : 'destructive'
                  }>
                    {phoneScreening.summary.overallRecommendation?.replace('_', ' ')}
                  </Badge>
                </div>
                
                <div>
                  <span className="font-medium">Availability:</span> {' '}
                  <span className="text-muted-foreground">{phoneScreening.summary.availability}</span>
                </div>
                
                <div>
                  <span className="font-medium">Compensation:</span> {' '}
                  <span className="text-muted-foreground">{phoneScreening.summary.compensation}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      
      {phoneScreening.status === 'failed' && (
        <div className="space-y-2">
          <Badge variant="destructive">Call Failed</Badge>
          <p className="text-sm text-muted-foreground">
            Unable to complete the phone screening. Please try again or use a different contact method.
          </p>
          <Button onClick={onStartScreening} variant="outline" size="sm">
            <Phone className="mr-2 h-4 w-4" />
            Retry Call
          </Button>
        </div>
      )}
    </div>
  );
}
